import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionQuery } from "../../../auth/hooks";
import { clientManagementApi } from "../api/clientManagement";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";
import { emptyCreateClientForm, type CreateClientFormState } from "../types";

export const ClientsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateClientFormState>(emptyCreateClientForm);

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const createClientMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      is_active: boolean;
      learning_locker_enabled: boolean;
      occupational_health_enabled: boolean;
      wearables_enabled: boolean;
      akea_life_enabled: boolean;
    }) => clientManagementApi.createClient(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: qk.clients() });
      setCreateForm(emptyCreateClientForm);
      setCreateOpen(false);
      navigate(`${ROUTES.SETTINGS_CLIENTS}/${response.data.client.id}`);
    },
  });

  const handleFormChange = (field: keyof CreateClientFormState, value: string | boolean) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  if (!isCentralAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Central admin access required to view all clients.</Alert>
      </Box>
    );
  }

  const clients = clientsQuery.data?.data ?? [];
  const filtered = search
    ? clients.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : clients;

  return (
    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Clients
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select a client to manage its branding, users, and settings.
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Search clients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320 }}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          New Client
        </Button>
      </Stack>

      {clientsQuery.isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading clients…</Typography>
        </Stack>
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {search ? "No clients match your search." : "No clients found."}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((client) => (
                <TableRow
                  key={client.id}
                  hover
                  onClick={() => navigate(`${ROUTES.SETTINGS_CLIENTS}/${client.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {client.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.is_active !== false ? "Active" : "Inactive"}
                      size="small"
                      color={client.is_active !== false ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create client dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Client name"
              value={createForm.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              autoFocus
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.is_active}
                  onChange={(e) => handleFormChange("is_active", e.target.checked)}
                />
              }
              label="Active"
            />
            <Typography variant="subtitle2">Enabled services</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.learning_locker_enabled}
                  onChange={(e) => handleFormChange("learning_locker_enabled", e.target.checked)}
                />
              }
              label="Learning Locker"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.occupational_health_enabled}
                  onChange={(e) => handleFormChange("occupational_health_enabled", e.target.checked)}
                />
              }
              label="Occupational Health"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.wearables_enabled}
                  onChange={(e) => handleFormChange("wearables_enabled", e.target.checked)}
                />
              }
              label="Wearables"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.akea_life_enabled}
                  onChange={(e) => handleFormChange("akea_life_enabled", e.target.checked)}
                />
              }
              label="Akea Life"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createForm.name.trim() || createClientMutation.isPending}
            onClick={() =>
              createClientMutation.mutate({
                name: createForm.name.trim(),
                is_active: createForm.is_active,
                learning_locker_enabled: createForm.learning_locker_enabled,
                occupational_health_enabled: createForm.occupational_health_enabled,
                wearables_enabled: createForm.wearables_enabled,
                akea_life_enabled: createForm.akea_life_enabled,
              })
            }
          >
            {createClientMutation.isPending ? "Creating…" : "Create client"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
